import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'

import { dateFnsLocale } from '@/utils/dateLocale'
import { Input } from '@/components/ui/Input'
import { CmExpertPdfControl } from '@/components/inspections/CmExpertPdfControl'
import { getPartnerCommentAiState } from '@/api/lead_summary'
import { leadCardCls, type LeadDraft } from '@/components/inspections/leadDetail'
import type { InspectionLeadRow } from '@/types/inspection-lead'

/** Блок партнёрского лида (СберАвто): данные анкеты (только чтение), адрес
 *  осмотра и квалификация ФО (правятся, сохраняются общей кнопкой «Сохранить»),
 *  исходные сообщения партнёра дословно. */
export function LeadPartnerSection({
  lead,
  draft,
  setDraft,
}: {
  lead: InspectionLeadRow
  draft: LeadDraft
  setDraft: Dispatch<SetStateAction<LeadDraft>>
}) {
  const { t } = useTranslation()
  // Ошибка Carlink AI при подготовке комментария: партнёру ничего не ушло.
  const aiQ = useQuery({
    queryKey: ['lead-partner-comment-ai', lead.id],
    queryFn: () => getPartnerCommentAiState(lead.id),
    refetchInterval: 60_000,
  })
  const aiError = aiQ.data?.error ? aiQ.data : null
  const fmtAt = (iso: string) => format(new Date(iso), 'd MMM yyyy, HH:mm', { locale: dateFnsLocale() })
  const row = (label: string, value: ReactNode) => (
    <div className="min-w-0">
      <div className="stryzh-input-label">{label}</div>
      <div className="mt-1 break-words text-sm text-[color:var(--stryzh-color-text-primary)]">{value || '—'}</div>
    </div>
  )
  return (
    <section className={leadCardCls}>
      <h2 className="stryzh-heading-card mb-4">{t('leads.detail.secSberAuto')}</h2>
      <div className="grid gap-3 sm:grid-cols-2">
        {row(t('leads.detail.sberCrm'), lead.partner_external_id)}
        {row(t('leads.detail.sberNewCar'), lead.trade_in_target_vehicle)}
      </div>
      <div className="mt-3">
        <Input
          label={t('leads.detail.sberAddress')}
          value={draft.inspection_address}
          onChange={(e) => setDraft((d) => ({ ...d, inspection_address: e.target.value }))}
          placeholder={t('leads.detail.sberAddressPh')}
        />
      </div>
      <label className="mt-3 flex flex-col text-left">
        <span className="stryzh-input-label">{t('leads.detail.sberNote')}</span>
        <textarea
          className="stryzh-input-field min-h-[72px] resize-y py-2 leading-snug"
          value={draft.qualification_note}
          onChange={(e) => setDraft((d) => ({ ...d, qualification_note: e.target.value }))}
          placeholder={t('leads.detail.sberNotePh')}
        />
      </label>
      <label className="mt-3 flex flex-col text-left">
        <span className="stryzh-input-label">
          {t('leads.detail.sberPartnerComment')}
          {lead.partner_comment_at ? (
            <span className="ml-2 font-normal normal-case text-[color:var(--stryzh-color-text-secondary)]">
              {t('leads.detail.sberPartnerCommentAt', {
                at: format(new Date(lead.partner_comment_at), 'd MMM yyyy, HH:mm', { locale: dateFnsLocale() }),
              })}
              {lead.partner_comment_by_ai && lead.partner_comment ? ` · ${t('leads.detail.sberPartnerCommentAi')}` : ''}
            </span>
          ) : null}
        </span>
        <textarea
          className="stryzh-input-field min-h-[72px] resize-y py-2 leading-snug"
          value={draft.partner_comment}
          onChange={(e) => setDraft((d) => ({ ...d, partner_comment: e.target.value }))}
          placeholder={t('leads.detail.sberPartnerCommentPh')}
        />
      </label>
      {aiError ? (
        <div className="mt-2 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
          {t('leads.detail.sberPartnerCommentAiError', {
            at: aiError.error_at ? fmtAt(aiError.error_at) : '',
          })}
          <div className="mt-1 break-words text-red-300/80">{aiError.error}</div>
        </div>
      ) : null}
      <CmExpertPdfControl lead={lead} />
      {lead.partner_messages?.length ? (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm text-[color:var(--stryzh-color-text-secondary)]">
            {t('leads.detail.sberMessages', { n: lead.partner_messages.length })}
          </summary>
          <div className="mt-2 flex flex-col gap-2">
            {lead.partner_messages.map((m, i) => (
              <div key={i} className="rounded border border-[color:var(--stryzh-color-border-default)] p-3">
                <div className="mb-1 text-xs text-[color:var(--stryzh-color-text-secondary)]">
                  {format(new Date(m.at), 'd MMM yyyy, HH:mm', { locale: dateFnsLocale() })}
                </div>
                <div className="whitespace-pre-wrap break-words text-sm text-[color:var(--stryzh-color-text-primary)]">{m.text}</div>
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </section>
  )
}
